import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { useCredits } from "@/context/CreditsContext";
import { toast } from "sonner";
import { Coins, Plus, ArrowUpRight, ArrowDownRight, Gift, Loader2, Lock, Crown } from "lucide-react";

const GOLD = "#E6B35A";
const RED = "#C8000F";

const PLAN_LABEL = { rookie: "Rookie", semi_pro: "Semi Pro", professional: "Professional", fan: "Fan (Spectator)", plus: "Plus (Spectator)", all_access: "All Access (Spectator)" };
const PLAN_BLURB = {
  rookie: "Free — pay as you go with credits",
  semi_pro: "$14.99/mo — run your whole season",
  professional: "$29.99/mo — full performance platform",
  fan: "$2.99/mo — follow up to 3 competitors",
  plus: "$5.99/mo — follow up to 10 competitors",
  all_access: "$9.99/mo — follow unlimited competitors",
};

const kindMeta = (k) => {
  if (k === "bonus") return { Icon: Gift, color: "#5FA83B", sign: "+" };
  if (k === "purchase") return { Icon: ArrowUpRight, color: "#5FA83B", sign: "+" };
  return { Icon: ArrowDownRight, color: RED, sign: "" };
};

export default function Wallet() {
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const { wallet, refresh } = useCredits();
  const [data, setData] = useState(wallet);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/credits/wallet").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (wallet) setData(wallet); }, [wallet]);

  const d = data || { credits: 0, plan: "rookie", packs: [], ledger: [], purchase_enabled: false };
  const packs = d.packs || [];
  const ledger = d.ledger || [];
  const enabled = d.purchase_enabled;

  const buy = async (pack) => {
    if (!enabled) { toast.info("Buying credits is coming soon — payments aren't live yet."); return; }
    setBuying(pack.id);
    try {
      const { data: res } = await api.post("/credits/checkout", { pack_id: pack.id, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.info("Buying credits is coming soon — payments aren't live yet.");
      setBuying(null);
    }
  };

  // palettes
  const page = isMobile ? "bg-[#0C1017] text-slate-100 min-h-[calc(100dvh_-_var(--hdr)_-_var(--ftr))]" : "bg-[#F6F7F9] text-slate-800 min-h-screen";
  const card = isMobile ? "bg-[#141A24] border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = isMobile ? "text-slate-400" : "text-slate-500";

  return (
    <div className={page} data-testid="wallet-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Balance hero */}
        <div className="relative overflow-hidden rounded-2xl border p-6 sm:p-8" style={{ background: "linear-gradient(135deg,#1A1205,#141A24)", borderColor: "rgba(230,179,90,0.4)" }}>
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full blur-3xl" style={{ background: "rgba(230,179,90,0.15)" }} />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold" style={{ color: GOLD }}>
                <Coins className="w-4 h-4" /> Rodeo Credits
              </div>
              <div className="mt-2 font-display text-5xl sm:text-6xl font-extrabold text-white" data-testid="wallet-balance">
                {(d.credits ?? 0).toLocaleString()}
              </div>
              <p className="mt-1 text-sm text-slate-300">1 credit ≈ $1 · spend on entries, draws & audits</p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full text-[#1A1205]" style={{ background: GOLD }} data-testid="wallet-plan">
              <Crown className="w-3.5 h-3.5" /> {PLAN_LABEL[d.plan] || "Rookie"}
            </span>
          </div>
        </div>

        {/* Plan / subscription */}
        <div className={`mt-6 rounded-2xl border p-5 flex items-center justify-between gap-4 ${card}`} data-testid="wallet-plan-card">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: GOLD }}>Your plan</div>
            <div className={`font-display text-xl font-bold ${isMobile ? "text-white" : "text-slate-900"}`}>{PLAN_LABEL[d.plan] || "Rookie"}</div>
            <div className={`text-sm ${sub}`}>{PLAN_BLURB[d.plan] || PLAN_BLURB.rookie}</div>
          </div>
          <button onClick={() => nav("/choose-plan")} data-testid="wallet-change-plan"
            className="shrink-0 inline-flex items-center gap-2 font-bold py-2.5 px-4 rounded-xl text-white transition-transform hover:scale-[1.02]" style={{ background: RED }}>
            <Crown className="w-4 h-4" /> {d.plan === "professional" ? "Manage plan" : "Upgrade"}
          </button>
        </div>

        {/* Buy credits */}
        <div className="mt-8">
          <h2 className={`font-display text-xl font-bold ${isMobile ? "text-white" : "text-slate-900"}`}>Buy more credits</h2>
          {!enabled && (
            <p className={`text-sm mt-1 ${sub}`} data-testid="wallet-buy-note">Credit purchases open soon — we're finishing payment setup.</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
            {packs.map((p) => {
              const per = p.amount / p.credits;
              const save = Math.round((1 - per) * 100);
              const hl = p.popular || p.best_value;
              return (
              <div key={p.id} data-testid={`wallet-pack-${p.id}`}
                className={`relative rounded-2xl border p-5 flex flex-col ${card} ${hl ? "ring-2" : ""}`}
                style={hl ? { borderColor: p.best_value ? GOLD : RED, boxShadow: `0 0 30px ${p.best_value ? "rgba(230,179,90,0.18)" : "rgba(200,0,15,0.15)"}` } : {}}>
                {p.popular && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide text-white px-2.5 py-0.5 rounded-full" style={{ background: RED }}>Popular</span>}
                {p.best_value && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wide px-2.5 py-0.5 rounded-full" style={{ background: GOLD, color: "#111" }}>Best Value</span>}
                <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: GOLD }}>{p.label}</div>
                <div className="mt-1 flex items-end gap-1.5">
                  <span className={`font-display text-3xl font-extrabold ${isMobile ? "text-white" : "text-slate-900"}`}>{p.credits}</span>
                  <span className={`text-sm mb-1 ${sub}`}>credits</span>
                </div>
                <div className={`mt-1 text-sm font-semibold ${isMobile ? "text-white" : "text-slate-900"}`}>${p.amount.toFixed(2)} {p.currency.toUpperCase()}</div>
                <div className={`text-[11px] ${sub}`}>${per.toFixed(2)} / credit{save > 0 ? ` · Save ${save}%` : ""}</div>
                <button onClick={() => buy(p)} disabled={buying === p.id} data-testid={`wallet-buy-${p.id}`}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-xl text-white disabled:opacity-60 transition-transform hover:scale-[1.02]"
                  style={{ background: enabled ? RED : "#6B7280" }}>
                  {buying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : enabled ? <Plus className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {enabled ? "Buy" : "Coming soon"}
                </button>
              </div>
              );
            })}
          </div>
        </div>

        {/* Ledger */}
        <div className="mt-8">
          <h2 className={`font-display text-xl font-bold ${isMobile ? "text-white" : "text-slate-900"}`}>History</h2>
          <div className={`mt-3 rounded-2xl border divide-y ${card} ${isMobile ? "divide-white/5" : "divide-[#EEF0F2]"}`} data-testid="wallet-ledger">
            {ledger.length === 0 ? (
              <div className={`px-5 py-8 text-center text-sm ${sub}`}>No activity yet. Your welcome credits are ready to spend.</div>
            ) : ledger.map((e, i) => {
              const { Icon, color, sign } = kindMeta(e.kind);
              return (
                <div key={i} className="flex items-center gap-3 px-5 py-3" data-testid={`wallet-ledger-row-${i}`}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isMobile ? "text-slate-200" : "text-slate-700"}`}>{e.reason}</div>
                    <div className={`text-xs ${sub}`}>{new Date(e.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                  </div>
                  <div className="font-display font-bold text-sm" style={{ color }}>{sign}{e.amount}</div>
                </div>
              );
            })}
          </div>
        </div>

        <button onClick={() => nav("/")} className={`mt-8 text-sm font-semibold ${isMobile ? "text-slate-400" : "text-slate-500 hover:text-slate-800"}`}>← Back to Rodeo Pilot</button>
      </div>
    </div>
  );
}
